import React, { useEffect, useState } from 'react';
import { useLoadingBar } from '../context/LoadingContext';

const ElectricLoadingBar = () => {
  const { getAggregateProgress, getCurrentMessage, isVisible, isLoading, registerTask, updateTask, completeTask } = useLoadingBar();
  const [displayProgress, setDisplayProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [mockLoadingStarted, setMockLoadingStarted] = useState(false);
  const [forceVisible, setForceVisible] = useState(false);

  const progress = getAggregateProgress();
  const message = getCurrentMessage();
  
  // Force visibility during mock loading
  const shouldShow = isVisible || mockLoadingStarted || forceVisible;

  // Mock loading for localhost development
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Only run once per page load
    const hasRunBefore = sessionStorage.getItem('mockLoadingRun');
    
    if (isLocalhost && !mockLoadingStarted && !isLoading && !hasRunBefore) {
      setMockLoadingStarted(true);
      setForceVisible(true);
      sessionStorage.setItem('mockLoadingRun', 'true');
      
      // Register mock loading tasks
      const mockTasks = [
        { id: 'mock_init', message: 'Initializing Monster Brains...', duration: 800 },
        { id: 'mock_3d', message: 'Loading 3D Assets...', duration: 1200 },
        { id: 'mock_city', message: 'Building City Grid...', duration: 1000 },
        { id: 'mock_chars', message: 'Loading Characters...', duration: 900 },
        { id: 'mock_final', message: 'Compiling Environment...', duration: 600 }
      ];

      mockTasks.forEach((task, index) => {
        setTimeout(() => {
          registerTask(task.id, 0);
          updateTask(task.id, 0, task.message);
          
          // Animate progress
          let progress = 0;
          const progressInterval = setInterval(() => {
            progress += Math.random() * 15 + 5; // Random progress increments
            if (progress >= 100) {
              progress = 100;
              clearInterval(progressInterval);
              setTimeout(() => completeTask(task.id), 200);
            }
            updateTask(task.id, progress, task.message);
          }, 100);
        }, index * 200);
      });
    }
  }, [isLoading, mockLoadingStarted, registerTask, updateTask, completeTask]);

  // Keyboard shortcut to trigger mock loading (Ctrl+L)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault();
        // Clear the session storage to allow mock loading again
        sessionStorage.removeItem('mockLoadingRun');
        // Reload the page to trigger mock loading
        window.location.reload();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Reset mock loading when all tasks complete (for localhost)
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalhost && !isLoading && mockLoadingStarted) {
      // Don't auto-reset - just let it complete once
      // The loading bar will fade out naturally after completion
    }
  }, [isLoading, mockLoadingStarted]);

  // Smooth progress animation
  useEffect(() => {
    if ((isLoading && isVisible) || mockLoadingStarted) {
      setIsAnimating(true);
      const targetProgress = progress;
      const startProgress = displayProgress;
      const duration = 300; // 300ms animation
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const animationProgress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - animationProgress, 3);
        const currentProgress = startProgress + (targetProgress - startProgress) * easeOutCubic;
        
        setDisplayProgress(currentProgress);

        if (animationProgress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    } else if (!isLoading) {
      // Fade out animation
      const fadeOutDuration = 500;
      const startTime = Date.now();
      const startProgress = displayProgress;

      const fadeOut = () => {
        const elapsed = Date.now() - startTime;
        const fadeProgress = Math.min(elapsed / fadeOutDuration, 1);
        
        // Ease out the progress bar
        const easeOut = 1 - Math.pow(1 - fadeProgress, 2);
        setDisplayProgress(startProgress * (1 - easeOut));

        if (fadeProgress < 1) {
          requestAnimationFrame(fadeOut);
        }
      };

      requestAnimationFrame(fadeOut);
    }
  }, [progress, isLoading, isVisible]);

  if (!isVisible && !mockLoadingStarted) return null;

  return (
    <>
      {/* Electric Blue Loading Bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        zIndex: 10003,
        background: 'rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(2px)'
      }}>
        {/* Progress Bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${displayProgress}%`,
          background: 'linear-gradient(90deg, #00aaff 0%, #33ccff 25%, #66ddff 50%, #33ccff 75%, #00aaff 100%)',
          backgroundSize: '200% 100%',
          boxShadow: `
            0 0 15px rgba(0, 170, 255, 0.9),
            0 0 25px rgba(51, 204, 255, 0.7),
            0 0 35px rgba(102, 221, 255, 0.5),
            inset 0 0 10px rgba(255, 255, 255, 0.3)
          `,
          transition: isAnimating ? 'none' : 'width 0.2s ease-out',
          animation: 'electricPulse 1.8s ease-in-out infinite, loadingShimmer 1.5s ease-in-out infinite'
        }} />

        {/* Electric Glow Effect */}
        <div style={{
          position: 'absolute',
          top: '-1px',
          left: 0,
          height: '5px',
          width: `${displayProgress}%`,
          background: 'linear-gradient(90deg, transparent 0%, rgba(0, 170, 255, 0.35) 50%, transparent 100%)',
          filter: 'blur(1px)',
          animation: 'electricGlow 1.5s ease-in-out infinite alternate'
        }} />

        {/* Inline status (left, small, electric blue) */}
        {message && isLoading && (
          <div style={{
            position: 'absolute',
            bottom: '6px',
            left: '8px',
            color: '#33ccff',
            fontSize: '10px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            letterSpacing: '0.2px',
            textShadow: '0 0 6px rgba(0,170,255,0.6)'
          }}>
            {message}
          </div>
        )}
      </div>

      {/* Removed floating message bubble */}

      {/* CSS Animations */}
      <style>{`
        @keyframes electricPulse {
          0%, 100% {
            box-shadow: 
              0 0 10px rgba(0, 170, 255, 0.8),
              0 0 20px rgba(51, 204, 255, 0.6),
              0 0 30px rgba(102, 221, 255, 0.4),
              inset 0 0 10px rgba(255, 255, 255, 0.2);
          }
          50% {
            box-shadow: 
              0 0 15px rgba(0, 170, 255, 1),
              0 0 25px rgba(51, 204, 255, 0.8),
              0 0 35px rgba(102, 221, 255, 0.6),
              inset 0 0 15px rgba(255, 255, 255, 0.3);
          }
        }

        @keyframes electricGlow {
          0% {
            opacity: 0.6;
            transform: scaleX(1);
          }
          100% {
            opacity: 1;
            transform: scaleX(1.02);
          }
        }

        @keyframes loadingShimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        @keyframes messageFade {
          0% {
            opacity: 0;
            transform: translateX(-50%) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default ElectricLoadingBar;
